// Re:Vault settings - a standalone page that opens as a tab inside the app. Controls write the
// shared store, which the app tab applies live.
//
// Nine sections (shared/settings-index.js), one shown at a time: a rail on the left from 900 px, a
// section picker from 600 px, and a list that opens one section at a time on phones. The address
// hash says what is open (#browsing, or #set=<row> for one row), so Back works and links land.
import { applyTheme } from "../app/theme.js";
import { mountLogo } from "../shared/brand.js";
import { subscribe, get, setMany } from "../shared/settings-store.js";
import { DEFAULTS } from "../shared/settings-schema.js";
import { applyObfuscation } from "../shared/obfuscate.js";
import { SECTIONS, SECTION_MAP, ENTRIES, ENTRY_MAP, isShown, searchSettings, resolveHash, sectionKeys } from "../shared/settings-index.js";
import { betaGates } from "../shared/beta-access.js";
import { row } from "./ui.js";
import { buildControls } from "./controls.js";
import { initExtensions } from "../app/extensions.js";
import { installConsentDialog } from "../shared/consent-gate.js";
import { keepFocusVisible } from "../shared/keep-focus-visible.js";
import { openShareSheet } from "../shared/share-gate.js";
import { heldOffer } from "./sections/carry-row.js";

const isMax = () => get("obfuscation") === "max";
applyTheme();
mountLogo(document.getElementById("rv-logo"));

const root = document.getElementById("rv-set-root");
const h = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const reduced = () => matchMedia("(prefers-reduced-motion: reduce)").matches || get("animations") === false || document.documentElement.dataset.perf === "on";
const phone = () => matchMedia("(max-width: 599px)").matches;
const CONTROLS = buildControls();

// --- the page skeleton ----------------------------------------------------------------------------
const live = h("div", "rv-sr-only");
live.setAttribute("role", "status");
const say = (t) => { live.textContent = ""; setTimeout(() => { live.textContent = t; }, 30); };

const rail = h("aside", "rv-set-rail");
const search = h("div", "rv-set-search");
const q = h("input", "rv-input rv-set-q");
q.type = "search";
q.id = "rv-set-q";
q.placeholder = "Search settings";
q.setAttribute("aria-label", "Search settings");
q.setAttribute("role", "combobox");
q.setAttribute("aria-autocomplete", "list");
q.setAttribute("aria-expanded", "false");
q.setAttribute("aria-controls", "rv-set-results");
q.autocomplete = "off";
q.spellcheck = false;
const results = h("div", "rv-set-results");
results.id = "rv-set-results";
results.setAttribute("role", "listbox");
results.setAttribute("aria-label", "Matching settings");
results.hidden = true;
search.append(q, results);

const nav = h("nav", "rv-set-nav");
nav.setAttribute("aria-label", "Settings sections");
const navList = h("ul", "");
const navLinks = new Map();
for (const s of SECTIONS) {
	const li = h("li", "");
	const a = h("a", "rv-set-link");
	a.href = "#" + s.id;
	a.dataset.sec = s.id;
	a.append(h("span", "rv-set-link-name", s.label), h("span", "rv-set-link-sum", s.summary));
	li.append(a);
	navList.append(li);
	navLinks.set(s.id, a);
}
nav.append(navList);
// 600 to 899 px: a picker instead of the rail
const pickWrap = h("label", "rv-set-pick");
pickWrap.append(h("span", "rv-sr-only", "Section"));
const pick = h("select", "rv-select");
for (const s of SECTIONS) { const o = h("option", "", s.label); o.value = s.id; pick.append(o); }
pick.addEventListener("change", () => go(pick.value, { focus: false }));
pickWrap.append(pick);
const railFoot = h("p", "rv-set-railfoot");
railFoot.innerHTML = '<a href="/help" target="_top">Help</a> · <a href="/status" target="_top">Status</a> · <a href="/privacy" target="_top">Privacy</a> · <a href="/accessibility" target="_top">Accessibility</a>';
rail.append(search, pickWrap, nav, railFoot);

const main = h("div", "rv-set-main");
const views = new Map(); // section id -> rendered <section>
root.textContent = "";
const shell = h("div", "rv-set-shell");
shell.append(rail, main);
// A carry code's last open that wasn't added yet: offered above everything, on every view, until
// it's dealt with.
root.append(heldOffer(), shell, live);

// --- sections -------------------------------------------------------------------------------------
const rows = new Map(); // entry id -> row element
const deps = new Map(); // parent id -> its children's group
const folds = new Map(); // folded group -> the keys its count watches

function entryRow(e) {
	const make = CONTROLS[e.id];
	let r;
	if (e.kind === "link") {
		r = row(e.label, e.desc, null);
		const b = h("button", "rv-btn rv-link-btn", "Go to " + e.label);
		b.type = "button";
		b.addEventListener("click", () => jump(e.to));
		r.querySelector(".rv-row-control").append(b);
		r.classList.add("rv-row-link");
	} else if (e.kind === "block") {
		r = row(e.label, e.desc, null);
		r.classList.add("rv-row-block");
		const c = make ? make() : null;
		if (c) { const box = r.querySelector(".rv-row-control"); box.append(c); }
		// a block's own controls name themselves; give the group a name for the screen reader
		if (c && !c.getAttribute("aria-label") && !c.getAttribute("aria-labelledby") && c.getAttribute("role") === "group") c.setAttribute("aria-labelledby", r.querySelector(".rv-row-label").id);
	} else {
		const c = make ? make() : null;
		r = row(e.label, e.desc, c);
		if (c && c.dataset && c.dataset.empty === "1") r.dataset.empty = "1";
	}
	r.dataset.set = e.id;
	r.id = "set-" + e.id;
	if (e.hint) {
		const hint = h("div", "rv-row-hint", e.hint);
		hint.id = r.id + "-hint";
		r.querySelector(".rv-row-text").append(hint);
	}
	rows.set(e.id, r);
	return r;
}

function renderSection(id) {
	if (views.has(id)) return views.get(id);
	const s = SECTION_MAP[id];
	const v = h("section", "rv-sec");
	v.id = "sec-" + id;
	v.hidden = true;
	v.setAttribute("aria-labelledby", "h-" + id);
	const head = h("div", "rv-sec-head");
	const back = h("button", "rv-sec-back");
	back.type = "button";
	back.setAttribute("aria-label", "All settings");
	back.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="10,3 5,8 10,13"/></svg>';
	back.addEventListener("click", toList);
	const title = h("div", "rv-sec-title");
	const h2 = h("h2", "", s.label);
	h2.id = "h-" + id;
	h2.tabIndex = -1;
	title.append(h2, h("p", "rv-sec-sum", s.summary));
	head.append(back, title);
	if (sectionKeys(id).length) {
		const reset = h("button", "rv-btn rv-sec-reset", "Reset this section");
		reset.type = "button";
		reset.addEventListener("click", () => resetSection(id, reset));
		head.append(reset);
	}
	v.append(head);
	let groupName = "", body = null;
	for (const e of ENTRIES) {
		if (e.section !== id) continue;
		if (e.group !== groupName) {
			groupName = e.group;
			const gh = h("h3", "rv-group", e.group);
			body = h("div", "rv-section-body");
			let grp;
			if (e.fold) {
				// folded shut on every visit; jump() opens it for a #set= link or a search result
				grp = h("details", "rv-grp rv-grp-fold");
				const sum = h("summary");
				gh.append(h("span", "rv-grp-on"));
				sum.append(gh);
				grp.append(sum, h("p", "rv-grp-note", e.fold), body);
				folds.set(grp, ENTRIES.filter((x) => x.section === id && x.group === e.group && x.key && !x.parent).map((x) => x.key));
			} else {
				grp = h("div", "rv-grp" + (e.danger ? " rv-danger" : ""));
				grp.append(gh, body);
			}
			v.append(grp);
		}
		const r = entryRow(e);
		if (e.parent && rows.has(e.parent)) {
			let d = deps.get(e.parent);
			if (!d) {
				d = h("div", "rv-dep");
				d.id = "dep-" + e.parent;
				d.setAttribute("role", "group");
				d.setAttribute("aria-label", ENTRY_MAP[e.parent].label + " options");
				const parentRow = rows.get(e.parent);
				parentRow.after(d);
				const ctl = parentRow.querySelector('.rv-row-control [role="switch"], .rv-row-control select');
				if (ctl) ctl.setAttribute("aria-controls", d.id);
				deps.set(e.parent, d);
			}
			d.append(r);
		} else body.append(r);
	}
	main.append(v);
	views.set(id, v);
	refreshDeps(false);
	return v;
}

// Show or hide sub-settings from the current values. Focus never moves; a short note says when
// options appear after a change made here.
function refreshDeps(announce) {
	for (const [pid, d] of deps) {
		let n = 0;
		for (const r of d.children) {
			const e = ENTRY_MAP[r.dataset.set];
			const on = isShown(e, get) && r.dataset.empty !== "1";
			if (r.hidden === on) r.hidden = !on;
			if (on) n++;
		}
		const was = !d.hidden;
		d.hidden = n === 0;
		const pr = rows.get(pid);
		const hint = pr && pr.querySelector(".rv-row-hint");
		if (hint) hint.hidden = n > 0;
		if (announce && !was && n > 0 && pr && pr.contains(document.activeElement)) say(n === 1 ? "1 more option" : n + " more options");
	}
	for (const [id, r] of rows) if (r.dataset.empty === "1" && !r.closest(".rv-dep")) r.hidden = true; else if (id === "keepHistory") paintHistoryNote(r);
	// a folded group says how many of its settings are changed, so nothing on stays out of sight
	for (const [g, keys] of folds) {
		const n = keys.filter((k) => { const v = get(k); return v !== DEFAULTS[k] && v !== false && v !== "off" && v !== "none"; }).length;
		g.querySelector(".rv-grp-on").textContent = n ? " (" + n + " on)" : "";
	}
	// a group whose rows are all hidden (Study Helper while it's switched off) hides its heading too
	for (const g of main.querySelectorAll(".rv-grp")) g.hidden = ![...g.querySelectorAll(".rv-section-body > .rv-row")].some((r) => !r.hidden);
}
function paintHistoryNote(r) {
	let n = r.querySelector(".rv-row-note");
	const on = get("incognito") === true;
	if (on && !n) { n = h("div", "rv-row-note", "Paused while incognito is on."); r.querySelector(".rv-row-text").append(n); }
	if (n) n.hidden = !on;
}
subscribe(() => refreshDeps(true));

// --- moving around ---------------------------------------------------------------------------------
let current = "";
function setView(kind) { document.body.dataset.view = kind; }
function show(id) {
	const v = renderSection(id);
	for (const [sid, el] of views) el.hidden = sid !== id;
	for (const [sid, a] of navLinks) { if (sid === id) a.setAttribute("aria-current", "page"); else a.removeAttribute("aria-current"); }
	pick.value = id;
	current = id;
	setView("section");
	return v;
}
function toList() {
	const from = current;
	if (location.hash) history.pushState(null, "", location.pathname + location.search);
	route();
	const a = navLinks.get(from);
	if (a && phone()) a.focus();
}
// Navigate to a section (changes the hash so Back works).
function go(id, opts = {}) {
	const want = "#" + id;
	if (location.hash !== want) history.pushState(null, "", want);
	show(id);
	window.scrollTo(0, 0);
	if (opts.focus !== false) { const t = document.getElementById("h-" + id); if (t) t.focus({ preventScroll: true }); }
}

let hitTimer = 0; // one outline at a time: a new jump must not lose its outline to an older timer
// Open the row's section, open whatever folds it, bring it to the middle, focus its control and
// outline it for a moment.
function jump(id) {
	const e = ENTRY_MAP[id];
	if (!e) return;
	const want = "#set=" + id;
	if (location.hash !== want) history.pushState(null, "", want);
	show(e.section);
	let target = e;
	let why = "";
	if (!isShown(e, get)) {
		for (let p = ENTRY_MAP[e.parent]; p; p = ENTRY_MAP[p.parent]) { target = p; if (isShown(p, get)) break; }
		const sw = typeof DEFAULTS[target.key] === "boolean";
		why = sw ? "Turn this on to change " + e.label + "." : e.label + " shows for some choices here.";
	}
	const r = rows.get(target.id);
	if (!r) return;
	for (let n = r.parentElement; n && n !== main; n = n.parentElement) {
		if (n.tagName === "DETAILS") n.open = true;
		if (n.fold) n.fold.open();
	}
	main.querySelectorAll(".rv-row-why").forEach((x) => x.remove());
	if (why) { const w = h("div", "rv-row-why", why); r.querySelector(".rv-row-text").append(w); }
	r.scrollIntoView({ block: "center", behavior: reduced() ? "auto" : "smooth" });
	// The first control that can take focus: not one inside a part of the row that's hidden right now.
	const inHidden = (c) => { for (let n = c; n && n !== r; n = n.parentElement) if (n.hidden) return true; return false; };
	const ctl = [...r.querySelectorAll('.rv-row-control :is(input:not([type="hidden"]):not([hidden]), select, button:not([disabled]):not([hidden]), summary, [tabindex="0"])')].find((c) => !inHidden(c));
	if (ctl) ctl.focus({ preventScroll: true });
	else { r.tabIndex = -1; r.focus({ preventScroll: true }); }
	main.querySelectorAll(".rv-hit").forEach((x) => x.classList.remove("rv-hit"));
	r.classList.add("rv-hit");
	if (reduced()) r.addEventListener("focusout", function off(ev) { if (!r.contains(ev.relatedTarget)) { r.classList.remove("rv-hit"); r.removeEventListener("focusout", off); } });
	else { clearTimeout(hitTimer); hitTimer = setTimeout(() => r.classList.remove("rv-hit"), 2000); }
	if (why) say(why);
}

function route() {
	// "#share" (the desktop's start menu, or a link): the Share row with its sheet open.
	if (location.hash === "#share") {
		history.replaceState(null, "", "#set=shareRevault");
		jump("shareRevault");
		openShareSheet();
		return;
	}
	const r = resolveHash(location.hash);
	if (r && r.id) { jump(r.id); return; }
	if (r) { show(r.section); return; }
	if (phone()) {
		const from = current;
		for (const el of views.values()) el.hidden = true;
		for (const a of navLinks.values()) a.removeAttribute("aria-current");
		current = "";
		setView("list");
		// browser Back from a section lands on the row you came from, like the back arrow does
		const a = from && navLinks.get(from);
		if (a && document.activeElement && (document.activeElement === document.body || !document.activeElement.offsetParent)) a.focus();
	} else show(current || "look");
}
window.addEventListener("hashchange", route);
window.addEventListener("popstate", route);
nav.addEventListener("click", (e) => {
	const a = e.target.closest("a[data-sec]");
	if (!a) return;
	e.preventDefault();
	go(a.dataset.sec);
});
// Arrow keys move along the rail, like a list.
nav.addEventListener("keydown", (e) => {
	const links = [...navLinks.values()];
	const i = links.indexOf(document.activeElement);
	if (i < 0) return;
	const j = e.key === "ArrowDown" ? i + 1 : e.key === "ArrowUp" ? i - 1 : e.key === "Home" ? 0 : e.key === "End" ? links.length - 1 : -2;
	if (j === -2) return;
	e.preventDefault();
	links[(j + links.length) % links.length].focus();
});
document.addEventListener("keydown", (e) => {
	if (e.key !== "Escape" || !phone() || document.body.dataset.view !== "section" || e.defaultPrevented) return;
	if (e.target.closest && e.target.closest(".rv-set-search, details, select")) return;
	toList();
});
matchMedia("(max-width: 599px)").addEventListener("change", route);
// An open Settings tab follows links from the app (app/tabs.js openSettings).
window.addEventListener("message", (e) => {
	if (e.origin !== location.origin || e.source !== window.parent || !e.data || e.data.type !== "revault-settings-goto") return;
	const hash = String(e.data.hash || "");
	if (!resolveHash(hash) && hash !== "#share") return;
	if (location.hash === hash) route(); else location.hash = hash;
});

// --- search ----------------------------------------------------------------------------------------
let found = [], sel = -1;
function renderResults() {
	const text = q.value.trim();
	found = text ? searchSettings(text, 12, betaGates()) : [];
	sel = found.length ? 0 : -1;
	results.textContent = "";
	if (!text) { closeResults(); return; }
	if (!found.length) {
		const none = h("div", "rv-set-none", "No settings match. Try another word.");
		results.append(none);
	}
	found.forEach((e, i) => {
		const o = h("div", "rv-set-opt");
		o.id = "rv-set-o" + i;
		o.dataset.id = e.id;
		o.setAttribute("role", "option");
		o.setAttribute("aria-selected", i === sel ? "true" : "false");
		const where = SECTION_MAP[e.section].label + " › " + (e.parent ? ENTRY_MAP[e.parent].label : e.group);
		o.append(h("span", "rv-set-opt-name", e.label), h("span", "rv-set-opt-where", where));
		o.addEventListener("mousedown", (ev) => ev.preventDefault());
		o.addEventListener("click", () => choose(i));
		results.append(o);
	});
	results.hidden = false;
	q.setAttribute("aria-expanded", "true");
	if (sel >= 0) q.setAttribute("aria-activedescendant", "rv-set-o" + sel); else q.removeAttribute("aria-activedescendant");
	say(found.length ? found.length + (found.length === 1 ? " setting" : " settings") : "No settings match.");
}
function closeResults() {
	results.hidden = true;
	q.setAttribute("aria-expanded", "false");
	q.removeAttribute("aria-activedescendant");
}
function move(d) {
	if (!found.length) return;
	sel = (sel + d + found.length) % found.length;
	[...results.children].forEach((o, i) => o.setAttribute("aria-selected", i === sel ? "true" : "false"));
	q.setAttribute("aria-activedescendant", "rv-set-o" + sel);
	const o = results.children[sel];
	if (o) o.scrollIntoView({ block: "nearest" });
}
function choose(i) {
	const e = found[i];
	if (!e) return;
	q.value = "";
	closeResults();
	jump(e.id);
}
q.addEventListener("input", renderResults);
q.addEventListener("focus", () => { if (q.value.trim()) renderResults(); });
q.addEventListener("blur", () => setTimeout(closeResults, 120));
q.addEventListener("keydown", (e) => {
	if (e.key === "ArrowDown") { e.preventDefault(); if (results.hidden) renderResults(); else move(1); }
	else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
	else if (e.key === "Enter") { e.preventDefault(); choose(sel); }
	else if (e.key === "Escape") {
		e.preventDefault();
		if (q.value) { q.value = ""; closeResults(); } else q.blur();
	}
});
// "/" or Ctrl+F on this page goes to the search box.
document.addEventListener("keydown", (e) => {
	const typing = /^(INPUT|SELECT|TEXTAREA)$/.test(document.activeElement && document.activeElement.tagName);
	if ((e.key === "/" && !typing && get("slashShortcut") !== false) || ((e.ctrlKey || e.metaKey) && (e.key === "f" || e.key === "F"))) { e.preventDefault(); q.focus(); q.select(); }
});

// --- reset this section ------------------------------------------------------------------------------
let toastEl = null, toastTimer = 0;
function toast(where, text, undo) {
	if (toastEl) toastEl.remove();
	clearTimeout(toastTimer);
	toastEl = h("div", "rv-set-toast");
	toastEl.setAttribute("role", "status");
	toastEl.append(h("span", "", text));
	if (undo) {
		const b = h("button", "rv-btn", "Undo");
		b.type = "button";
		b.addEventListener("click", () => { undo(); toastEl.remove(); toastEl = null; say("Undone."); where.focus(); });
		toastEl.append(b);
	}
	// Right after the button in reading order, so Tab reaches Undo.
	where.after(toastEl);
	toastTimer = setTimeout(() => { if (toastEl && !toastEl.contains(document.activeElement)) { toastEl.remove(); toastEl = null; } }, 10000);
}
function resetSection(id, btn) {
	const keys = sectionKeys(id).filter((k) => JSON.stringify(get(k)) !== JSON.stringify(DEFAULTS[k]));
	const name = SECTION_MAP[id].label;
	if (!keys.length) { toast(btn, "Everything in " + name + " is already at its default."); return; }
	if (!confirm("Reset " + keys.length + (keys.length === 1 ? " setting" : " settings") + " in " + name + " to " + (keys.length === 1 ? "its default" : "their defaults") + "? Your saved data stays.")) return;
	const before = {}, patch = {};
	for (const k of keys) { before[k] = get(k); patch[k] = Array.isArray(DEFAULTS[k]) ? DEFAULTS[k].slice() : DEFAULTS[k]; }
	setMany(patch);
	toast(btn, name + " is back to its defaults.", () => setMany(before));
}

// --- go ------------------------------------------------------------------------------------------------
route();
applyObfuscation(isMax());
installConsentDialog(); // the warning before Vencord (beta) and the like turn on
initExtensions(); // relay extension toggles to the SW when settings is opened standalone
// Keep this page's own theme + obfuscation in sync when tweaked here.
subscribe(() => { applyTheme(); applyObfuscation(isMax()); });
keepFocusVisible();
